import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import type { ReturnFinancials } from "@babackapp/return-widget-components/hooks/use-return-financials";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { Separator } from "@babackapp/ui/components/separator";

/**
 * Shop-now receipt: the customer buys new items with their credit, so the balance is
 * what the backend actually charges (`amountDue` = rebuy − exchange credit − refund −
 * discount). Return fee, bonus and initial shipping are left out on purpose so the
 * announced balance stays the amount Stripe captures.
 */
export function RebuyReceipt({
  financials,
  t,
}: {
  financials: ReturnFinancials;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  const { summary } = financials;
  // Only exchange-credit contributes to the balance. Classic exchanges
  // (exchangeVariantTotal) are cost-neutral — the returned item and the new variant
  // cancel out — so they must NOT count as credit here.
  const credit =
    summary.refundTotal + summary.exchangeCreditTotal + summary.discountTotal;
  const net = summary.rebuyTotal - credit;
  const balanceLabel =
    net > 0
      ? t("completeSummary.remainingToPay")
      : net < 0
        ? t("completeSummary.netRefund")
        : t("completeSummary.balance");

  return (
    <>
      {summary.refundTotal > 0 && (
        <Line label={t("financial.refund")}>
          {formatPrice(summary.refundTotal, summary.currency)}
        </Line>
      )}
      {summary.exchangeCreditTotal > 0 && (
        <Line label={t("financial.exchangeCredit")}>
          {formatPrice(summary.exchangeCreditTotal, summary.currency)}
        </Line>
      )}
      {summary.discountTotal > 0 && (
        <Line label={t("financial.discount")}>
          {formatPrice(summary.discountTotal, summary.currency)}
        </Line>
      )}
      <Line label={t("completeSummary.newItemsLabel")}>
        −{formatPrice(summary.rebuyTotal, summary.currency)}
      </Line>
      <Separator />
      <Line label={balanceLabel} emphasis>
        {formatPrice(Math.abs(net), summary.currency)}
      </Line>
    </>
  );
}

function Line({
  label,
  emphasis,
  children,
}: {
  label: string;
  emphasis?: boolean;
  children: React.ReactNode;
}) {
  const emphasisClass = emphasis ? "font-semibold text-foreground" : undefined;
  return (
    <ReceiptLine>
      <ReceiptLineLabel className={emphasisClass}>{label}</ReceiptLineLabel>
      <ReceiptLineValue className={emphasisClass}>{children}</ReceiptLineValue>
    </ReceiptLine>
  );
}
